import React from 'react'
import { setValue } from '../../utils/input'
import { useCallbackRef } from '../../utils/useCallbackRef'
import { AddressValues, getStreetAndHouseNumber } from './utils'

export interface StreetReturnValues {
  ref: React.RefCallback<HTMLInputElement>
  value?: string
  address: AddressValues | undefined
  getProps: (props?: StreetGetPropsAttributes) => StreetGetProps
}

export interface StreetProps {
  value?: string
}

export interface StreetGetPropsAttributes {
  error?: string
}

export interface StreetGetProps {
  id?: string
  type?: HTMLInputElement['type']
  inputRef: React.RefCallback<HTMLInputElement>
  error?: boolean
  helperText?: string
  name: string
}

const useStreet = ({ value = '' }: StreetProps): StreetReturnValues => {
  const { callbackRef, elementRef } = useCallbackRef<HTMLInputElement>(
    (element) => {
      if (element !== null) {
        setValue(element, value)
      }
    },
  )

  const [address, setAddress] = React.useState(() =>
    getStreetAndHouseNumber(value),
  )

  const handleStreetChange = React.useCallback(() => {
    setAddress(getStreetAndHouseNumber(elementRef.current?.value))
  }, [elementRef])

  React.useEffect(() => {
    if (!elementRef.current) {
      return undefined
    }

    const inputRef = elementRef.current

    inputRef.addEventListener('input', handleStreetChange)

    return () => {
      inputRef.removeEventListener('input', handleStreetChange)
    }
  }, [elementRef, handleStreetChange])

  React.useEffect(() => {
    setAddress(getStreetAndHouseNumber(value))

    if (!elementRef.current) {
      return
    }

    setValue(elementRef.current, value)
  }, [value, elementRef])

  const getProps = React.useCallback(
    (props): StreetGetProps => {
      const { name, id = name } = props

      return {
        id,
        name,
        inputRef: callbackRef,
        ...props,
      }
    },
    [callbackRef],
  )

  return {
    ref: callbackRef,
    value,
    address,
    getProps,
  }
}

export { useStreet }
